import type { DeletedMember, WorkspaceMember } from './types';

/**
 * t471u: an Owner deletes a deactivated person permanently. Shared by the web (desktop + phone) and the iOS app so the
 * wording and the name check are identical; the API repeats the check.
 */

/** Their full name, typed exactly: case and outer spaces don't matter, and runs of spaces count as one. */
export function deleteNameMatches(typed: string, name: string): boolean {
  const norm = (s: string) => s.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-GB');
  const want = norm(name ?? '');
  return want.length > 0 && norm(typed ?? '') === want;
}

/** What the members list calls someone who was deleted. */
export const DELETED_USER_NAME = 'Deleted user';

/**
 * The members map used to show names and photos (byId), with deleted people added as "Deleted user" (no photo). The
 * list of people to pick from stays as it is.
 */
export function membersById(members: WorkspaceMember[], deleted: DeletedMember[] = [], ws = ''): Map<string, WorkspaceMember> {
  const map = new Map<string, WorkspaceMember>();
  for (const d of deleted) {
    map.set(d.user_id, {
      user_id: d.user_id, workspace_id: ws, email: null, display_name: d.display_name || DELETED_USER_NAME, avatar_path: null, avatar_url: null, job_title: null,
      phone: null, timezone: 'Europe/London', status_text: null, status_emoji: null, status_expires_at: null, role: 'member', status: 'deactivated', joined_at: '',
    });
  }
  for (const m of members) map.set(m.user_id, m);
  return map;
}

export const MEMBER_DELETE_TEXT = {
  button: 'Delete permanently',
  title: (name: string) => `Delete ${name} permanently?`,
  deleted: [
    'Their profile, contact details and photo',
    'HR record, documents, bank details and emergency contacts',
    'Devices, invites, preferences and personal to-dos',
  ],
  deletedLead: 'This deletes:',
  kept: 'Messages and comments stay, shown as from \u2018Deleted user\u2019. Shifts, timesheets and time-off history are kept without their name for your records.',
  elsewhere: 'If they\u2019re in another workspace, only this workspace\u2019s details are deleted.',
  undo: 'This can\u2019t be undone.',
  label: (name: string) => `Type ${name} to confirm`,
  confirm: 'Delete',
  cancel: 'Cancel',
  done: 'Deleted',
  failed: 'Couldn\u2019t delete them. Try again.',
} as const;
